import {
  Box,
  Button,
  Card,
  Collapsible,
  Flex,
  Grid,
  HStack,
  Input,
  NativeSelect,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import { ChevronDown } from 'lucide-react'
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import { Field as SharedField } from '@mangovoice/mango-ui'

import { useGetDeviceModels } from '../hooks/useDeviceModels'
import {
  useGetDirectories,
  useGetExtensionsLine,
  useGetSystemDefaults,
  useGetUsers,
} from '../hooks/useDeviceReferenceData'
import { useMacValidation } from '../hooks/useMacValidation'
import { useDevicesI18n } from '../i18n'
import {
  type CreateDevicePayload,
  isWifiRequiredDeviceModel,
  type ProvisioningDeviceDetail,
} from '../types/device.types'
import {
  filterUsersWithLineExtensions,
  getAssignedUserId,
  getTransportHostValidationError,
  type LineExtensionOption,
} from '../utils/helpers'
import { formatMac, normalizeMac } from '../utils/mac'
import {
  ExtensionSelect,
  MacAddressInput,
  ModelSelect,
  StationLabelInput,
  UserSelect,
} from './DeviceFormFields'
import { DevicePreviewCard } from './DevicePreviewCard'

const TRANSPORT_OPTIONS = [
  { value: 'UDP', label: 'UDP' },
  { value: 'TCP', label: 'TCP' },
  { value: 'TLS', label: 'TLS' },
]

const JITTER_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
]

const TIMEZONE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'Alaska', label: 'Alaska' },
  { value: 'Arizona', label: 'Arizona' },
  { value: 'Atlantic Time (Canada)', label: 'Atlantic Time (Canada)' },
  {
    value: 'Central Time (US & Canada)',
    label: 'Central Time (US & Canada)',
  },
  {
    value: 'Eastern Time (US & Canada)',
    label: 'Eastern Time (US & Canada)',
  },
  {
    value: 'Greenwich Mean Time (UK)',
    label: 'Greenwich Mean Time (UK)',
  },
  { value: 'Hawaii', label: 'Hawaii' },
  { value: 'Indiana (East)', label: 'Indiana (East)' },
  {
    value: 'Mountain Time (US & Canada)',
    label: 'Mountain Time (US & Canada)',
  },
  {
    value: 'Pacific Time (US & Canada)',
    label: 'Pacific Time (US & Canada)',
  },
  {
    value: 'America/Vancouver',
    label: 'Pacific Time - British Columbia (PCT)',
  },
  { value: 'Puerto Rico', label: 'Puerto Rico' },
]

const BOOLEAN_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
]

const SERVER_GROUP_HOST_OPTIONS = [
  { value: 'primary', label: 'Primary' },
  { value: 'secondary', label: 'Secondary' },
]

const NAT_TRAVERSAL_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'stun', label: 'STUN' },
]

const SOURCE_OPTIONS = [
  { value: 'mango-new', label: 'Mango New' },
  { value: 'mango-refurbished', label: 'Mango Refurbished' },
  { value: 'customer', label: 'Customer' },
] as const

const WIFI_SECURITY_MODE_OPTIONS = [
  { value: 'NONE', label: 'NONE' },
  { value: 'WEP', label: 'WEP' },
  { value: 'WPA', label: 'WPA/WPA2 PSK' },
  { value: 'WPA3', label: 'WPA3-Personal' },
] as const

export function getSuggestedStationLabel(
  assignedUserId: number | '',
  primaryExtensionId: number | '',
  users: { id: number; full_name: string }[],
  extensions: { id: number; number: string }[],
): string {
  if (assignedUserId === '') return ''
  const user = users.find((u) => u.id === assignedUserId)
  if (!user) return ''
  const ext = extensions.find((e) => e.id === primaryExtensionId)
  if (ext) return `${user.full_name} - ${ext.number}`
  return user.full_name
}

export interface DeviceFormState {
  device_model: number | ''
  assigned_user_id: number | ''
  primary_extension_id: number | ''
  corporate_directory: number | ''
  source: 'mango-new' | 'mango-refurbished' | 'customer'
  warranty_expires_at: string
  device_mac_address: string
  station_label: string
  time_zone: string
  dst_enable: boolean
  transport: string
  jitter: string
  server_group_host: string
  allow_provision_retrieval: boolean
  sidecar_enabled: boolean
  nat_method: boolean
  pages: number | ''
  line_button_count: number | ''
  wifi_enabled: boolean
  wifi_network_ssid: string
  wifi_security_mode: 'NONE' | 'WEP' | 'WPA' | 'WPA3'
  wifi_password: string
  acoustic_shield_enabled: boolean
}

function deviceToFormState(
  device: ProvisioningDeviceDetail | null,
  defaultTimeZone: string,
): DeviceFormState {
  if (!device) {
    return {
      device_model: '',
      assigned_user_id: '',
      primary_extension_id: '',
      corporate_directory: '',
      source: 'mango-new',
      warranty_expires_at: '',
      device_mac_address: '',
      station_label: '',
      time_zone: defaultTimeZone,
      dst_enable: true,
      transport: 'UDP',
      jitter: 'normal',
      server_group_host: 'primary',
      allow_provision_retrieval: true,
      sidecar_enabled: false,
      nat_method: false,
      pages: 1,
      line_button_count: '',
      wifi_enabled: false,
      wifi_network_ssid: '',
      wifi_security_mode: 'NONE',
      wifi_password: '',
      acoustic_shield_enabled: false,
    }
  }

  return {
    device_model: device.device_model,
    assigned_user_id: device.assigned_user?.id ?? '',
    primary_extension_id: device.primary_extension?.id ?? '',
    corporate_directory: device.corporate_directory ?? '',
    source:
      device.source === 'mango-new' ||
      device.source === 'mango-refurbished' ||
      device.source === 'customer'
        ? device.source
        : 'customer',
    warranty_expires_at: device.warranty_expires_at
      ? String(device.warranty_expires_at).slice(0, 10)
      : '',
    device_mac_address: formatMac(device.device_mac_address),
    station_label: device.station_label,
    time_zone: device.time_zone ?? defaultTimeZone,
    dst_enable: device.dst_enable ?? true,
    transport: device.transport ?? 'UDP',
    jitter: device.jitter ?? 'normal',
    server_group_host: device.server_group_host ?? 'primary',
    allow_provision_retrieval: device.allow_provision_retrieval ?? true,
    sidecar_enabled: device.sidecar_enabled ?? false,
    nat_method: device.nat_method ?? false,
    pages: device.pages ?? 1,
    line_button_count: device.line_button_count ?? '',
    wifi_enabled: device.wifi_enabled ?? false,
    wifi_network_ssid: device.wifi_network_ssid ?? '',
    wifi_security_mode: device.wifi_security_mode ?? 'NONE',
    wifi_password: device.wifi_password ?? '',
    acoustic_shield_enabled: device.acoustic_shield_enabled ?? false,
  }
}

function formStateToCreatePayload(
  state: DeviceFormState,
  options?: { includeInternalFields?: boolean },
): CreateDevicePayload {
  const includeInternalFields = options?.includeInternalFields ?? true
  const stationLabel = state.station_label.trim()
  const payload: CreateDevicePayload = {
    device_model: state.device_model as number,
    device_mac_address: normalizeMac(state.device_mac_address),
    station_label: stationLabel,
    name: stationLabel,
    has_new_config: false,
    time_zone: state.time_zone || undefined,
    dst_enable: state.dst_enable,
    transport: state.transport || 'UDP',
    jitter: state.jitter || 'normal',
    allow_provision_retrieval: state.allow_provision_retrieval,
    sidecar_enabled: state.sidecar_enabled,
    nat_method: state.nat_method,
  }

  if (state.assigned_user_id !== '') {
    payload.assigned_user_id = state.assigned_user_id
  }
  if (state.primary_extension_id !== '') {
    payload.primary_extension_id = state.primary_extension_id
  }
  if (state.corporate_directory !== '') {
    payload.corporate_directory = state.corporate_directory
  }
  if (includeInternalFields) {
    payload.source = state.source
    payload.warranty_expires_at = state.warranty_expires_at || null
  }
  if (state.server_group_host.trim()) {
    payload.server_group_host = state.server_group_host.trim()
  }
  payload.wifi_enabled = state.wifi_enabled
  payload.wifi_network_ssid = state.wifi_network_ssid.trim() || undefined
  payload.wifi_security_mode = state.wifi_security_mode
  payload.wifi_password = state.wifi_password
  payload.acoustic_shield_enabled = state.acoustic_shield_enabled
  return payload
}

function formatProvisionedTime(value?: string): string {
  if (!value) return ''
  const dt = new Date(value)
  if (Number.isNaN(dt.getTime())) return value
  return dt.toLocaleString()
}

export interface DeviceFormHandle {
  submit: (reboot?: boolean) => void
}

export interface DeviceFormProps {
  mode: 'add' | 'edit'
  pbxId: number
  device: ProvisioningDeviceDetail | null
  isLoadingDevice?: boolean
  onSubmit: (payload: CreateDevicePayload, reboot?: boolean) => void
  onCancel: () => void
  isSubmitting: boolean
  onModelChange?: (modelId: number | null) => void
  onSidecarChange?: (enabled: boolean) => void
  onPrimaryExtensionChange?: (extensionId: number | null) => void
  hideActions?: boolean
  onCanSubmitChange?: (canSubmit: boolean) => void
  onDirtyChange?: (dirty: boolean) => void
  onValidationMessagesChange?: (messages: string[]) => void
  onLineButtonCountChange?: (count: number | null) => void
  /** Mirrors legacy behavior for internal-only fields (Warranty/Source). */
  showInternalFields?: boolean
}

const DeviceFormInner = React.forwardRef<DeviceFormHandle, DeviceFormProps>(
  (
    {
      mode,
      pbxId,
      device,
      isLoadingDevice,
      onSubmit,
      onCancel,
      isSubmitting,
      onModelChange,
      onSidecarChange,
      onPrimaryExtensionChange,
      hideActions,
      onCanSubmitChange,
      onDirtyChange,
      onValidationMessagesChange,
      onLineButtonCountChange,
      showInternalFields = true,
    },
    ref,
  ) => {
    const { text } = useDevicesI18n()
    const { data: models = [] } = useGetDeviceModels()
    const { data: systemDefaults } = useGetSystemDefaults(pbxId)
    const { data: directories = [] } = useGetDirectories(pbxId)
    const { data: extensions = [] } = useGetExtensionsLine(pbxId)
    const { data: users = [] } = useGetUsers(pbxId)

    const defaultTimeZone =
      systemDefaults?.default_time_zone ?? 'Mountain Time (US & Canada)'

    const [formState, setFormState] = useState<DeviceFormState>(() =>
      deviceToFormState(device, defaultTimeZone),
    )
    const [transportHostError, setTransportHostError] = useState<string | null>(
      null,
    )
    const isDirtyRef = useRef(false)
    const lastSuggestedStationLabelRef = useRef<string>('')
    const defaultTimeZoneRef = useRef(defaultTimeZone)
    const onDirtyChangeRef = useRef(onDirtyChange)
    const onCanSubmitChangeRef = useRef(onCanSubmitChange)
    const onValidationMessagesChangeRef = useRef(onValidationMessagesChange)
    const onModelChangeRef = useRef(onModelChange)
    const onSidecarChangeRef = useRef(onSidecarChange)
    const onPrimaryExtensionChangeRef = useRef(onPrimaryExtensionChange)
    const onLineButtonCountChangeRef = useRef(onLineButtonCountChange)

    useEffect(() => {
      defaultTimeZoneRef.current = defaultTimeZone
    }, [defaultTimeZone])

    useEffect(() => {
      onDirtyChangeRef.current = onDirtyChange
    }, [onDirtyChange])

    useEffect(() => {
      onCanSubmitChangeRef.current = onCanSubmitChange
    }, [onCanSubmitChange])

    useEffect(() => {
      onValidationMessagesChangeRef.current = onValidationMessagesChange
    }, [onValidationMessagesChange])

    useEffect(() => {
      onModelChangeRef.current = onModelChange
    }, [onModelChange])

    useEffect(() => {
      onSidecarChangeRef.current = onSidecarChange
    }, [onSidecarChange])

    useEffect(() => {
      onPrimaryExtensionChangeRef.current = onPrimaryExtensionChange
    }, [onPrimaryExtensionChange])

    useEffect(() => {
      onLineButtonCountChangeRef.current = onLineButtonCountChange
    }, [onLineButtonCountChange])

    const currentModel = models.find((m) => m.id === formState.device_model)
    const modelWithCompatFlags = currentModel as
      (typeof currentModel & { supports_wifi_toggle?: boolean }) | undefined
    const supportsWifi =
      (currentModel?.supports_wifi ?? false) ||
      (modelWithCompatFlags?.supports_wifi_toggle ?? false)
    const wifiRequired = isWifiRequiredDeviceModel(
      currentModel ?? formState.device_model,
    )
    const supportsAcousticShield =
      currentModel?.supports_acoustic_shield ?? false
    const supportsNat = currentModel?.supports_nat ?? true
    const supportsSidecar = currentModel?.sidecar_supported ?? false

    const isFormDisabled = formState.device_model === ''
    const lineKeysPerPage =
      formState.device_model !== '' &&
      currentModel?.buttons_per_page != null &&
      currentModel.buttons_per_page > 0
        ? currentModel.buttons_per_page
        : undefined
    const showLineButtonCountSettings =
      lineKeysPerPage != null && lineKeysPerPage > 0
    const computedTotalLineKeys = showLineButtonCountSettings
      ? (Number(formState.pages || 1) || 1) * lineKeysPerPage
      : null

    const maxPages = useMemo(() => {
      if (!showLineButtonCountSettings || !currentModel?.pages) {
        return 0
      }
      return currentModel.pages > 0 ? currentModel.pages : 0
    }, [showLineButtonCountSettings, currentModel?.pages])

    const pageOptions = useMemo(() => {
      if (maxPages <= 0) {
        return []
      }
      const base = Array.from({ length: maxPages }, (_, i) => i + 1)
      if (
        formState.pages !== '' &&
        Number.isFinite(formState.pages) &&
        !base.includes(formState.pages)
      ) {
        return [...base, formState.pages].sort((a, b) => a - b)
      }
      return base
    }, [maxPages, formState.pages])

    const effectiveLineButtonCount = useMemo(() => {
      if (
        currentModel &&
        currentModel.pages != null &&
        currentModel.pages > 0 &&
        currentModel.buttons_per_page != null &&
        currentModel.buttons_per_page > 0
      ) {
        const selectedPages = Number(formState.pages || 1) || 1
        return selectedPages * currentModel.buttons_per_page
      }
      return currentModel?.dss_key_count ?? null
    }, [
      currentModel,
      formState.pages,
      currentModel?.pages,
      currentModel?.buttons_per_page,
      currentModel?.dss_key_count,
    ])

    const typedExtensions = extensions as LineExtensionOption[]
    const hasAssignedUserMetadata = typedExtensions.some(
      (ext) => getAssignedUserId(ext) !== undefined,
    )
    // Legacy Admin only lists users that have at least one line extension
    // assigned in the "Assigned User" select. In edit mode, if the device is
    // currently assigned to a user without extensions (stale data), keep that
    // user in the list so the current value still renders rather than silently
    // appearing unassigned.
    const filteredUsers = useMemo(() => {
      const base = filterUsersWithLineExtensions(users, typedExtensions)
      if (
        mode === 'edit' &&
        formState.assigned_user_id !== '' &&
        !base.some((u) => u.id === formState.assigned_user_id)
      ) {
        const current = users.find((u) => u.id === formState.assigned_user_id)
        if (current) return [...base, current]
      }
      return base
    }, [users, typedExtensions, mode, formState.assigned_user_id])

    // Mirror legacy buildGenericLineExtensionSelect: the Primary Line
    // Extension dropdown is empty until an Assigned User is chosen, and once a
    // user is selected only their line extensions are listed. Inactive
    // extensions are already removed upstream by useGetExtensionsLine.
    const filteredPrimaryExtensions = useMemo(() => {
      if (formState.assigned_user_id === '') {
        return [] as LineExtensionOption[]
      }
      if (!hasAssignedUserMetadata) {
        return typedExtensions
      }
      return typedExtensions.filter(
        (ext) => getAssignedUserId(ext) === formState.assigned_user_id,
      )
    }, [hasAssignedUserMetadata, typedExtensions, formState.assigned_user_id])

    const { macError, macValidating, setMacError, validateMac, clearMacError } =
      useMacValidation({
        pbxId,
        deviceId: mode === 'edit' && device ? device.id : undefined,
        messages: {
          invalid: text('devices.form.macInvalid', 'MAC address is invalid'),
          alreadyInUse: text(
            'devices.form.macAlreadyInUse',
            'MAC address already in use',
          ),
        },
      })

    useEffect(() => {
      if (device !== undefined && !isLoadingDevice) {
        setFormState(deviceToFormState(device, defaultTimeZoneRef.current))
        isDirtyRef.current = false
        onDirtyChangeRef.current?.(false)
      }
    }, [device, isLoadingDevice])

    // When system defaults load, update only the timezone (don't reset the whole form)
    useEffect(() => {
      if (mode !== 'add' || isDirtyRef.current) return
      setFormState((prev) => {
        if (prev.time_zone === defaultTimeZone) return prev
        return { ...prev, time_zone: defaultTimeZone }
      })
    }, [mode, defaultTimeZone])

    useEffect(() => {
      if (formState.assigned_user_id === '') return

      if (!hasAssignedUserMetadata) return

      if (filteredPrimaryExtensions.length === 0) {
        if (formState.primary_extension_id === '') return
        setFormState((prev) => ({ ...prev, primary_extension_id: '' }))
        return
      }

      const currentExtensionIsValid = filteredPrimaryExtensions.some(
        (ext) => ext.id === formState.primary_extension_id,
      )
      if (currentExtensionIsValid) return

      setFormState((prev) => ({
        ...prev,
        primary_extension_id: filteredPrimaryExtensions[0].id,
      }))
    }, [
      formState.assigned_user_id,
      formState.primary_extension_id,
      filteredPrimaryExtensions,
      hasAssignedUserMetadata,
    ])

    useEffect(() => {
      const suggested = getSuggestedStationLabel(
        formState.assigned_user_id,
        formState.primary_extension_id,
        users,
        filteredPrimaryExtensions,
      )
      const prevSuggested = lastSuggestedStationLabelRef.current
      lastSuggestedStationLabelRef.current = suggested
      setFormState((prev) => {
        const shouldAutoUpdate =
          prev.station_label === '' || prev.station_label === prevSuggested
        if (shouldAutoUpdate && prev.station_label !== suggested) {
          return { ...prev, station_label: suggested }
        }
        return prev
      })
    }, [
      formState.assigned_user_id,
      formState.primary_extension_id,
      users,
      filteredPrimaryExtensions,
    ])

    useEffect(() => {
      if (directories.length === 0) return
      if (formState.corporate_directory !== '') return

      setFormState((prev) => {
        if (prev.corporate_directory !== '') return prev
        return { ...prev, corporate_directory: directories[0].id }
      })
    }, [directories, formState.corporate_directory])

    useEffect(() => {
      setFormState((prev) => {
        if (showLineButtonCountSettings) {
          const nextPages = prev.pages === '' ? 1 : prev.pages
          const nextLineCount = (Number(nextPages) || 1) * lineKeysPerPage
          if (
            prev.pages !== nextPages ||
            prev.line_button_count !== nextLineCount
          ) {
            return {
              ...prev,
              pages: nextPages,
              line_button_count: nextLineCount,
            }
          }
          return prev
        }

        if (prev.pages !== '' || prev.line_button_count !== '') {
          return { ...prev, pages: '', line_button_count: '' }
        }

        return prev
      })
    }, [showLineButtonCountSettings, lineKeysPerPage])

    useEffect(() => {
      onModelChangeRef.current?.(
        formState.device_model === '' ? null : formState.device_model,
      )
    }, [formState.device_model])

    useEffect(() => {
      onSidecarChangeRef.current?.(formState.sidecar_enabled)
    }, [formState.sidecar_enabled])

    useEffect(() => {
      if (!currentModel || supportsSidecar || !formState.sidecar_enabled) return
      setFormState((prev) => ({ ...prev, sidecar_enabled: false }))
    }, [currentModel, supportsSidecar, formState.sidecar_enabled])

    useEffect(() => {
      setFormState((prev) => {
        const next: DeviceFormState = { ...prev }
        let changed = false

        // WiFi-required (AX83H) does not need the model catalog to be loaded —
        // formState.device_model / id match is enough to force WiFi on.
        if (wifiRequired) {
          if (!next.wifi_enabled) {
            next.wifi_enabled = true
            changed = true
          }
        } else if (currentModel && !supportsWifi) {
          if (next.wifi_enabled) {
            next.wifi_enabled = false
            changed = true
          }
          if (
            next.wifi_network_ssid !== '' ||
            next.wifi_security_mode !== 'NONE' ||
            next.wifi_password !== ''
          ) {
            next.wifi_network_ssid = ''
            next.wifi_security_mode = 'NONE'
            next.wifi_password = ''
            changed = true
          }
        }

        if (
          currentModel &&
          !supportsAcousticShield &&
          next.acoustic_shield_enabled
        ) {
          next.acoustic_shield_enabled = false
          changed = true
        }

        if (currentModel && !supportsNat && next.nat_method) {
          next.nat_method = false
          changed = true
        }

        return changed ? next : prev
      })
    }, [
      currentModel,
      supportsWifi,
      wifiRequired,
      supportsAcousticShield,
      supportsNat,
    ])

    useEffect(() => {
      onPrimaryExtensionChangeRef.current?.(
        formState.primary_extension_id === ''
          ? null
          : formState.primary_extension_id,
      )
    }, [formState.primary_extension_id])

    useEffect(() => {
      onLineButtonCountChangeRef.current?.(effectiveLineButtonCount)
    }, [effectiveLineButtonCount])

    const updateField = useCallback(
      <K extends keyof DeviceFormState>(
        field: K,
        value: DeviceFormState[K],
      ) => {
        setFormState((prev) => ({ ...prev, [field]: value }))
        if (!isDirtyRef.current) {
          isDirtyRef.current = true
          onDirtyChangeRef.current?.(true)
        }
        if (field === 'transport' || field === 'server_group_host') {
          setTransportHostError(null)
        }
        if (field === 'device_mac_address') {
          clearMacError()
        }
      },
      [clearMacError],
    )

    const handleModelChange = useCallback(
      (value: number | '') => {
        setFormState((prev) => {
          const next: DeviceFormState = { ...prev, device_model: value }
          const selected = models.find((m) => m.id === value)
          const previous = models.find((m) => m.id === prev.device_model)
          const selectedRequiresWifi = isWifiRequiredDeviceModel(
            selected ?? value,
          )
          const selectedSupportsWifi = selected?.supports_wifi ?? false

          // Apply WiFi defaults in the same update that changes the model so
          // credential fields don't wait for a follow-up effect (no flicker).
          if (selectedRequiresWifi) {
            next.wifi_enabled = true
          } else if (!selectedSupportsWifi) {
            next.wifi_enabled = false
            next.wifi_network_ssid = ''
            next.wifi_security_mode = 'NONE'
            next.wifi_password = ''
          } else if (isWifiRequiredDeviceModel(previous ?? prev.device_model)) {
            // Leaving a WiFi-required model: drop the forced-on default.
            next.wifi_enabled = false
          }

          return next
        })
        if (!isDirtyRef.current) {
          isDirtyRef.current = true
          onDirtyChangeRef.current?.(true)
        }
      },
      [models],
    )

    const handleMacBlur = useCallback(() => {
      void validateMac(formState.device_mac_address)
    }, [validateMac, formState.device_mac_address])

    const handleSubmit = useCallback(
      async (reboot?: boolean) => {
        const mac = formState.device_mac_address.trim()
        if (!mac) {
          setMacError(
            text('devices.form.macRequired', 'MAC address is required'),
          )
          return
        }

        const macValid = await validateMac(mac)
        if (!macValid) return

        if (!formState.station_label.trim()) {
          return
        }

        if (formState.primary_extension_id === '') {
          return
        }

        const hostValidationError = getTransportHostValidationError(
          formState.transport,
          formState.server_group_host,
        )
        if (hostValidationError) {
          setTransportHostError(hostValidationError)
          return
        }

        if (mode === 'add') {
          const payload = formStateToCreatePayload(formState, {
            includeInternalFields: showInternalFields,
          })
          if (showLineButtonCountSettings) {
            if (formState.pages !== '') payload.pages = formState.pages
            if (formState.line_button_count !== '') {
              payload.line_button_count = formState.line_button_count
            }
          }
          onSubmit(payload)
        } else {
          const payload = formStateToCreatePayload(formState, {
            includeInternalFields: showInternalFields,
          })
          if (showLineButtonCountSettings) {
            if (formState.pages !== '') payload.pages = formState.pages
            if (formState.line_button_count !== '') {
              payload.line_button_count = formState.line_button_count
            }
          }
          onSubmit(payload, reboot)
        }
      },
      [
        formState,
        mode,
        onSubmit,
        validateMac,
        setMacError,
        setTransportHostError,
        showLineButtonCountSettings,
      ],
    )

    const handleSubmitNoReboot = useCallback(() => {
      void handleSubmit(false)
    }, [handleSubmit])

    const handleSubmitReboot = useCallback(() => {
      void handleSubmit(true)
    }, [handleSubmit])

    useImperativeHandle(
      ref,
      () => ({
        submit: (reboot?: boolean) => void handleSubmit(reboot),
      }),
      [handleSubmit],
    )

    const isLoading = isLoadingDevice
    const currentTransportHostError = getTransportHostValidationError(
      formState.transport,
      formState.server_group_host,
    )
    const shouldShowLoadedFieldErrors = mode === 'edit' && !isLoadingDevice
    const modelError =
      shouldShowLoadedFieldErrors && formState.device_model === ''
        ? text('devices.form.modelRequired', 'Device model is required.')
        : undefined
    const macRequiredError =
      shouldShowLoadedFieldErrors &&
      formState.device_mac_address.trim().length === 0
        ? text('devices.form.macRequired', 'MAC address is required')
        : undefined
    const assignedUserError =
      shouldShowLoadedFieldErrors && formState.assigned_user_id === ''
        ? text(
            'devices.form.assignedUserRequired',
            'Assigned user is required.',
          )
        : undefined
    const directoryError =
      shouldShowLoadedFieldErrors && formState.corporate_directory === ''
        ? text('devices.form.directoryRequired', 'Directory is required.')
        : undefined
    const stationLabelError =
      shouldShowLoadedFieldErrors && formState.station_label.trim().length === 0
        ? text('devices.form.stationNameRequired', 'Station name is required.')
        : undefined
    const primaryExtensionError =
      formState.assigned_user_id !== '' &&
      hasAssignedUserMetadata &&
      filteredPrimaryExtensions.length === 0
        ? text(
            'devices.form.noExtensionsForUser',
            'The assigned user does not have any valid line extensions. Select a different user or assign a line extension before saving.',
          )
        : undefined
    const primaryExtensionRequiredError =
      shouldShowLoadedFieldErrors &&
      formState.assigned_user_id !== '' &&
      !primaryExtensionError &&
      formState.primary_extension_id === ''
        ? text(
            'devices.form.primaryLineExtensionRequired',
            'Primary line extension is required.',
          )
        : undefined
    const validationMessages = useMemo(
      () =>
        [
          modelError,
          macRequiredError,
          assignedUserError,
          directoryError,
          stationLabelError,
          primaryExtensionError,
          primaryExtensionRequiredError,
          currentTransportHostError ?? undefined,
          macError ?? undefined,
        ].filter((message): message is string => Boolean(message)),
      [
        assignedUserError,
        currentTransportHostError,
        directoryError,
        macError,
        macRequiredError,
        modelError,
        primaryExtensionError,
        primaryExtensionRequiredError,
        stationLabelError,
      ],
    )
    const canSubmit =
      formState.device_model !== '' &&
      formState.device_mac_address.trim().length > 0 &&
      formState.station_label.trim().length > 0 &&
      formState.assigned_user_id !== '' &&
      formState.primary_extension_id !== '' &&
      formState.corporate_directory !== '' &&
      !macError &&
      !currentTransportHostError

    useEffect(() => {
      onCanSubmitChangeRef.current?.(canSubmit)
    }, [canSubmit])

    useEffect(() => {
      onValidationMessagesChangeRef.current?.(validationMessages)
    }, [validationMessages])

    if (isLoading) {
      return (
        <Card.Root>
          <Card.Body p={6}>
            <HStack justify="center" py={8}>
              <Spinner size="lg" />
              <Text color="fg.muted">
                {text('devices.form.loadingDevice', 'Loading device...')}
              </Text>
            </HStack>
          </Card.Body>
        </Card.Root>
      )
    }

    return (
      <VStack align="stretch" gap={6}>
        <Flex gap={6} align="stretch" wrap="wrap">
          <Card.Root w="full" maxW="400px" flex="0 1 400px">
            <Card.Header p={6}>
              <Card.Title>
                {mode === 'add'
                  ? text('devices.form.addDevice', 'Add device')
                  : text('devices.form.editDevice', 'Edit device')}
              </Card.Title>
            </Card.Header>
            <Card.Body p={6}>
              <VStack gap={6} align="stretch">
                <Text fontSize="md" fontWeight="semibold" color="fg.muted">
                  {text('devices.form.deviceInfo', 'Device Info')}
                </Text>

                <ModelSelect
                  value={formState.device_model}
                  onChange={handleModelChange}
                  models={models}
                  error={modelError}
                  required
                />

                <MacAddressInput
                  value={formState.device_mac_address}
                  onChange={(value) => updateField('device_mac_address', value)}
                  onBlur={handleMacBlur}
                  onEnterKey={handleSubmitNoReboot}
                  error={macRequiredError ?? macError}
                  validating={macValidating}
                  disabled={isFormDisabled}
                  required
                  confirmOnGenerate={mode === 'edit'}
                  showProvisioningCopy={mode === 'edit'}
                />

                {showInternalFields && (
                  <>
                    <SharedField.Root>
                      <SharedField.Label>
                        {text(
                          'devices.form.warrantyExpiresAt',
                          'Warranty Expires At',
                        )}
                      </SharedField.Label>
                      <Input
                        type="date"
                        size="sm"
                        value={formState.warranty_expires_at}
                        onChange={(e) =>
                          updateField('warranty_expires_at', e.target.value)
                        }
                        disabled={isFormDisabled}
                        css={{
                          '&::-webkit-calendar-picker-indicator': {
                            backgroundImage: 'revert',
                          },
                        }}
                      />
                    </SharedField.Root>

                    <SharedField.Root>
                      <SharedField.Label>
                        {text('devices.form.source', 'Source')}
                      </SharedField.Label>
                      <NativeSelect.Root size="sm" disabled={isFormDisabled}>
                        <NativeSelect.Field
                          value={formState.source}
                          onChange={(e) =>
                            updateField(
                              'source',
                              e.target.value as DeviceFormState['source'],
                            )
                          }
                        >
                          {SOURCE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                    </SharedField.Root>
                  </>
                )}

                {mode === 'edit' && (
                  <>
                    <SharedField.Root>
                      <SharedField.Label>
                        {text(
                          'devices.form.lastProvisionedTime',
                          'Last Provisioned Time',
                        )}
                      </SharedField.Label>
                      <Input
                        size="sm"
                        value={formatProvisionedTime(
                          device?.last_provisioned_time,
                        )}
                        disabled
                      />
                    </SharedField.Root>

                    <SharedField.Root>
                      <SharedField.Label>
                        {text(
                          'devices.form.lastProvisionedFrom',
                          'Last Provisioned From',
                        )}
                      </SharedField.Label>
                      <Input
                        size="sm"
                        value={device?.last_provisioned_from ?? ''}
                        disabled
                      />
                    </SharedField.Root>
                  </>
                )}
              </VStack>
            </Card.Body>
          </Card.Root>
          <Box w="full" flex="1 1 216px" minW="216px">
            <DevicePreviewCard model={currentModel ?? null} />
          </Box>
        </Flex>

        <Collapsible.Root defaultOpen={false}>
          <Card.Root>
            <Collapsible.Trigger asChild>
              <Card.Header p={4} cursor="pointer" userSelect="none">
                <HStack justify="space-between">
                  <Card.Title>
                    {text('devices.form.advanced', 'Advanced')}
                  </Card.Title>
                  <Collapsible.Indicator
                    transition="transform 0.2s"
                    _open={{ transform: 'rotate(-180deg)' }}
                  >
                    <ChevronDown size={18} />
                  </Collapsible.Indicator>
                </HStack>
              </Card.Header>
            </Collapsible.Trigger>
            <Collapsible.Content>
              <Card.Body p={6} pt={0} css={{ containerType: 'inline-size' }}>
                <Grid
                  templateColumns="repeat(2, minmax(0, 1fr))"
                  gap={4}
                  css={{
                    '@container (max-width: 640px)': {
                      gridTemplateColumns: 'minmax(0, 1fr)',
                    },
                  }}
                >
                  {showLineButtonCountSettings && (
                    <>
                      <SharedField.Root>
                        <SharedField.Label>
                          {text('devices.form.pages', 'Pages')}
                        </SharedField.Label>
                        <NativeSelect.Root size="sm" disabled={isFormDisabled}>
                          <NativeSelect.Field
                            value={String(formState.pages)}
                            onChange={(e) =>
                              updateField(
                                'pages',
                                e.target.value ? Number(e.target.value) : '',
                              )
                            }
                          >
                            {pageOptions.map((page) => (
                              <option key={page} value={page}>
                                {page}
                              </option>
                            ))}
                          </NativeSelect.Field>
                          <NativeSelect.Indicator />
                        </NativeSelect.Root>
                      </SharedField.Root>

                      <SharedField.Root>
                        <SharedField.Label>
                          {text(
                            'devices.form.lineKeysAvailable',
                            'Line Keys Available',
                          )}
                        </SharedField.Label>
                        <NativeSelect.Root size="sm" disabled>
                          <NativeSelect.Field
                            value={
                              computedTotalLineKeys != null
                                ? String(computedTotalLineKeys)
                                : ''
                            }
                            onChange={() => {}}
                          >
                            {computedTotalLineKeys != null ? (
                              <option value={computedTotalLineKeys}>
                                {computedTotalLineKeys}
                              </option>
                            ) : (
                              <option value="">
                                {text(
                                  'devices.form.selectModelFirst',
                                  'Select Model First...',
                                )}
                              </option>
                            )}
                          </NativeSelect.Field>
                          <NativeSelect.Indicator />
                        </NativeSelect.Root>
                        {computedTotalLineKeys != null && (
                          <Text color="fg.muted" fontSize="sm" mt={1}>
                            {text(
                              'devices.form.lineKeysHelper',
                              '({{pages}} page{{pageSuffix}} x {{keysPerPage}} keys = {{total}} total)',
                              {
                                pages: formState.pages || 1,
                                pageSuffix: formState.pages === 1 ? '' : 's',
                                keysPerPage: lineKeysPerPage,
                                total: computedTotalLineKeys,
                              },
                            )}
                          </Text>
                        )}
                      </SharedField.Root>
                    </>
                  )}

                  <SharedField.Root>
                    <SharedField.Label>
                      {text(
                        'devices.form.allowProvisioning',
                        'Allow Provisioning',
                      )}
                    </SharedField.Label>
                    <NativeSelect.Root size="sm" disabled={isFormDisabled}>
                      <NativeSelect.Field
                        value={String(formState.allow_provision_retrieval)}
                        onChange={(e) =>
                          updateField(
                            'allow_provision_retrieval',
                            e.target.value === 'true',
                          )
                        }
                      >
                        {BOOLEAN_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </SharedField.Root>

                  {supportsSidecar && (
                    <SharedField.Root>
                      <SharedField.Label>
                        {text('devices.form.sidecarEnabled', 'Sidecar Enabled')}
                      </SharedField.Label>
                      <NativeSelect.Root size="sm" disabled={isFormDisabled}>
                        <NativeSelect.Field
                          value={String(formState.sidecar_enabled)}
                          onChange={(e) =>
                            updateField(
                              'sidecar_enabled',
                              e.target.value === 'true',
                            )
                          }
                        >
                          {BOOLEAN_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                    </SharedField.Root>
                  )}

                  <SharedField.Root>
                    <SharedField.Label>
                      {text('devices.form.transport', 'Transport')}
                    </SharedField.Label>
                    <NativeSelect.Root size="sm" disabled={isFormDisabled}>
                      <NativeSelect.Field
                        value={formState.transport}
                        onChange={(e) =>
                          updateField('transport', e.target.value)
                        }
                      >
                        {TRANSPORT_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </SharedField.Root>

                  <SharedField.Root>
                    <SharedField.Label>
                      {text('devices.form.jitter', 'Jitter')}
                    </SharedField.Label>
                    <NativeSelect.Root size="sm" disabled={isFormDisabled}>
                      <NativeSelect.Field
                        value={formState.jitter}
                        onChange={(e) => updateField('jitter', e.target.value)}
                      >
                        {JITTER_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </SharedField.Root>

                  <SharedField.Root>
                    <SharedField.Label>
                      {text(
                        'devices.form.serverGroupHost',
                        'Server Group Host',
                      )}
                    </SharedField.Label>
                    <NativeSelect.Root size="sm" disabled={isFormDisabled}>
                      <NativeSelect.Field
                        value={formState.server_group_host}
                        onChange={(e) =>
                          updateField('server_group_host', e.target.value)
                        }
                      >
                        {SERVER_GROUP_HOST_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                    {(transportHostError || currentTransportHostError) && (
                      <Text color="fg.error" fontSize="sm" mt={1}>
                        {transportHostError || currentTransportHostError}
                      </Text>
                    )}
                  </SharedField.Root>

                  {supportsNat && (
                    <SharedField.Root>
                      <SharedField.Label>
                        {text('devices.form.natTraversal', 'NAT Traversal')}
                      </SharedField.Label>
                      <NativeSelect.Root size="sm" disabled={isFormDisabled}>
                        <NativeSelect.Field
                          value={formState.nat_method ? 'stun' : 'normal'}
                          onChange={(e) =>
                            updateField('nat_method', e.target.value === 'stun')
                          }
                        >
                          {NAT_TRAVERSAL_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                    </SharedField.Root>
                  )}

                  {supportsWifi && !wifiRequired && (
                    <SharedField.Root>
                      <SharedField.Label>
                        {text('devices.form.wifiEnabled', 'WiFi Enabled')}
                      </SharedField.Label>
                      <NativeSelect.Root size="sm" disabled={isFormDisabled}>
                        <NativeSelect.Field
                          value={String(formState.wifi_enabled)}
                          onChange={(e) =>
                            updateField(
                              'wifi_enabled',
                              e.target.value === 'true',
                            )
                          }
                        >
                          {BOOLEAN_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                    </SharedField.Root>
                  )}

                  {(supportsWifi || wifiRequired) && formState.wifi_enabled && (
                    <>
                      <SharedField.Root>
                        <SharedField.Label>
                          {text(
                            'devices.form.wifiNetworkSsid',
                            'WiFi Network SSID',
                          )}
                        </SharedField.Label>
                        <Input
                          size="sm"
                          value={formState.wifi_network_ssid}
                          onChange={(e) =>
                            updateField('wifi_network_ssid', e.target.value)
                          }
                          disabled={isFormDisabled}
                        />
                      </SharedField.Root>

                      <SharedField.Root>
                        <SharedField.Label>
                          {text(
                            'devices.form.wifiSecurityMode',
                            'WiFi Security Mode',
                          )}
                        </SharedField.Label>
                        <NativeSelect.Root size="sm" disabled={isFormDisabled}>
                          <NativeSelect.Field
                            value={formState.wifi_security_mode}
                            onChange={(e) =>
                              updateField(
                                'wifi_security_mode',
                                e.target
                                  .value as DeviceFormState['wifi_security_mode'],
                              )
                            }
                          >
                            {WIFI_SECURITY_MODE_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </NativeSelect.Field>
                          <NativeSelect.Indicator />
                        </NativeSelect.Root>
                      </SharedField.Root>

                      <SharedField.Root>
                        <SharedField.Label>
                          {text('devices.form.wifiPassword', 'WiFi Password')}
                        </SharedField.Label>
                        <Input
                          size="sm"
                          type="password"
                          value={formState.wifi_password}
                          onChange={(e) =>
                            updateField('wifi_password', e.target.value)
                          }
                          disabled={isFormDisabled}
                        />
                      </SharedField.Root>
                    </>
                  )}

                  {supportsAcousticShield && (
                    <SharedField.Root>
                      <SharedField.Label>
                        {text(
                          'devices.form.acousticShieldEnabled',
                          'Acoustic Shield Enabled',
                        )}
                      </SharedField.Label>
                      <NativeSelect.Root size="sm" disabled={isFormDisabled}>
                        <NativeSelect.Field
                          value={String(formState.acoustic_shield_enabled)}
                          onChange={(e) =>
                            updateField(
                              'acoustic_shield_enabled',
                              e.target.value === 'true',
                            )
                          }
                        >
                          {BOOLEAN_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                    </SharedField.Root>
                  )}
                </Grid>
              </Card.Body>
            </Collapsible.Content>
          </Card.Root>
        </Collapsible.Root>

        <Collapsible.Root defaultOpen={true}>
          <Card.Root>
            <Collapsible.Trigger asChild>
              <Card.Header p={4} cursor="pointer" userSelect="none">
                <HStack justify="space-between">
                  <Card.Title>
                    {text('devices.form.displaySettings', 'Display Settings')}
                  </Card.Title>
                  <Collapsible.Indicator
                    transition="transform 0.2s"
                    _open={{ transform: 'rotate(-180deg)' }}
                  >
                    <ChevronDown size={18} />
                  </Collapsible.Indicator>
                </HStack>
              </Card.Header>
            </Collapsible.Trigger>
            <Collapsible.Content>
              <Card.Body p={6} pt={0} css={{ containerType: 'inline-size' }}>
                <Grid
                  templateColumns="repeat(2, minmax(0, 1fr))"
                  gap={4}
                  css={{
                    '@container (max-width: 640px)': {
                      gridTemplateColumns: 'minmax(0, 1fr)',
                    },
                  }}
                >
                  <UserSelect
                    value={formState.assigned_user_id}
                    onChange={(value) => updateField('assigned_user_id', value)}
                    users={filteredUsers}
                    error={assignedUserError}
                    required
                    disabled={isFormDisabled}
                  />

                  <SharedField.Root required invalid={Boolean(directoryError)}>
                    <SharedField.Label>
                      {text('devices.form.directory', 'Directory')}
                    </SharedField.Label>
                    <NativeSelect.Root
                      size="sm"
                      disabled={isFormDisabled}
                      invalid={Boolean(directoryError)}
                    >
                      <NativeSelect.Field
                        value={String(formState.corporate_directory)}
                        onChange={(e) =>
                          updateField(
                            'corporate_directory',
                            e.target.value ? Number(e.target.value) : '',
                          )
                        }
                      >
                        {directories.map((directory) => (
                          <option key={directory.id} value={directory.id}>
                            {directory.name}
                          </option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                    {directoryError && (
                      <SharedField.ErrorText>
                        {directoryError}
                      </SharedField.ErrorText>
                    )}
                  </SharedField.Root>

                  <ExtensionSelect
                    value={formState.primary_extension_id}
                    onChange={(value) =>
                      updateField('primary_extension_id', value)
                    }
                    extensions={filteredPrimaryExtensions}
                    error={
                      primaryExtensionError ?? primaryExtensionRequiredError
                    }
                    disabled={isFormDisabled}
                    required
                  />

                  <SharedField.Root>
                    <SharedField.Label>
                      {text('devices.form.timeZone', 'Time Zone')}
                    </SharedField.Label>
                    <NativeSelect.Root size="sm" disabled={isFormDisabled}>
                      <NativeSelect.Field
                        value={formState.time_zone}
                        onChange={(e) =>
                          updateField('time_zone', e.target.value)
                        }
                      >
                        {TIMEZONE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </SharedField.Root>

                  <StationLabelInput
                    value={formState.station_label}
                    onChange={(value) => updateField('station_label', value)}
                    error={stationLabelError}
                    required
                    disabled={isFormDisabled}
                  />

                  <SharedField.Root>
                    <SharedField.Label>
                      {text(
                        'devices.form.enableDaylightSavings',
                        'Enable Daylight Savings',
                      )}
                    </SharedField.Label>
                    <NativeSelect.Root size="sm" disabled={isFormDisabled}>
                      <NativeSelect.Field
                        value={String(formState.dst_enable)}
                        onChange={(e) =>
                          updateField('dst_enable', e.target.value === 'true')
                        }
                      >
                        {BOOLEAN_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </SharedField.Root>
                </Grid>
              </Card.Body>
            </Collapsible.Content>
          </Card.Root>
        </Collapsible.Root>

        {!hideActions && (
          <Box p={6} pt={4} borderTopWidth={1} borderColor="border.default">
            <HStack gap={3} justifyContent="flex-end">
              <Button
                type="button"
                variant="outline"
                colorPalette="neutral"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                {text('devices.common.cancel', 'Cancel')}
              </Button>
              {mode === 'add' ? (
                <Button
                  type="button"
                  colorPalette="brand"
                  onClick={handleSubmitNoReboot}
                  loading={isSubmitting}
                  disabled={!canSubmit}
                >
                  {text('devices.form.createDevice', 'Create device')}
                </Button>
              ) : (
                <>
                  <Button
                    type="button"
                    colorPalette="brand"
                    onClick={handleSubmitNoReboot}
                    loading={isSubmitting}
                    disabled={!canSubmit}
                  >
                    {text('devices.common.save', 'Save')}
                  </Button>
                  <Button
                    type="button"
                    colorPalette="brand"
                    variant="outline"
                    onClick={handleSubmitReboot}
                    loading={isSubmitting}
                    disabled={!canSubmit}
                  >
                    {text('devices.common.saveAndReboot', 'Save & Reboot')}
                  </Button>
                </>
              )}
            </HStack>
          </Box>
        )}
      </VStack>
    )
  },
)

DeviceFormInner.displayName = 'DeviceFormInner'
export const DeviceForm = React.memo(DeviceFormInner)
